import type { Ref } from 'vue'

import { TaskStatus } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorTaskModal } from './tutor-tasks/enums'
import { useDisciplineTasksStore } from './tasks/store'
import { useTutorTaskUpdateApi } from '../api/tutor-tasks/update.hook'
import { useTutorTaskDeleteApi } from '../api/tutor-tasks/delete.hook'
import { useDisciplineTasksRefresh } from './discipline-tasks-refresh.hook'

interface ModalDisciplineTaskConfirmReturn {
  refIsCancelOpen: ComputedRef<boolean>
  refIsDeleteOpen: ComputedRef<boolean>
  refIsDeclinedOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refCancelDetail: ComputedRef<string>
  refDeleteDetail: ComputedRef<string>
  refTaskTypeLabel: ComputedRef<string>
  closeModal: () => void
  cancelTask: () => Promise<void>
  deleteTask: () => Promise<void>
}

/** The three one-step task dialogs: cancel, delete and "cannot be postponed". */
export const useModalDisciplineTaskConfirm = (): ModalDisciplineTaskConfirmReturn => {
  const store = useDisciplineTasksStore()
  const listsStore = useListsStore()
  const { writeAndRefresh } = useDisciplineTasksRefresh()

  const refIsLoading = ref(false)

  const refTask = computed(() => store.getActiveTask)
  const refIsCancelOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.Cancel)
  const refIsDeleteOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.Delete)
  const refIsDeclinedOpen = computed<boolean>(() => store.getActiveModal === TutorTaskModal.PostponeDeclined)

  const refCancelDetail = computed<string>(() =>
    `Are you sure you want to cancel Task ${refTask.value?.id ?? ''}? This action cannot be undone.`)
  const refDeleteDetail = computed<string>(() =>
    `Are you sure you want to delete Task ${refTask.value?.id ?? ''}? This action cannot be undone.`)
  const refTaskTypeLabel = computed<string>(() =>
    (refTask.value && listsStore.getLists?.tutor_task.type[refTask.value.type]) || '')

  const closeModal = (): void => {
    if (refIsLoading.value) return
    store.closeModal()
  }

  const cancelTask = async (): Promise<void> => {
    const task = refTask.value
    if (!task || refIsLoading.value) return

    await writeAndRefresh(refIsLoading, () => useTutorTaskUpdateApi({
      taskId: task.id,
      payload: { status: TaskStatus.Canceled },
      successMessage: 'The task was successfully cancel.',
    }))
  }

  const deleteTask = async (): Promise<void> => {
    const task = refTask.value
    if (!task || refIsLoading.value) return

    await writeAndRefresh(refIsLoading, () => useTutorTaskDeleteApi({ taskId: task.id }))
  }

  return {
    refIsCancelOpen,
    refIsDeleteOpen,
    refIsDeclinedOpen,
    refIsLoading,
    refCancelDetail,
    refDeleteDetail,
    refTaskTypeLabel,
    closeModal,
    cancelTask,
    deleteTask,
  }
}
